import {
    Breadcrumbs,
    Button,
    Grid,
    IconButton, Paper,
    Skeleton,
    Stack,
    Switch, Table, TableBody,
    TableCell, TableContainer, TableHead,
    TableRow, Tooltip,
    Typography
} from "@mui/material";
import {EditIcon, ElectronicsIcon, LockerIcon, PasswordResetIcon, StockIcon, TrashIcon} from "../../../common/Icons";
import {EquipmentConnectionStatus} from "./components/EquipmentConnectionStatus";
import {checkPermissionsAndRedirect, hasPermission} from "../../../../configs/functions";
import React, {useEffect, useMemo, useState} from "react";
import {Add, History, Home, PictureAsPdf, RemoveRedEye} from "@mui/icons-material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useAppContext} from "../../../../contexts/AppContext";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useSearchParams} from "react-router-dom";
import {
    atualizaEquipamento, atualizaLocker, carregaConfigsEquipamento, carregaEquipamentos,
    carregaLocker,
    carregaLockers,
    carregaPontosEntrega, deletaLocker,
    generateUpdateTokenEquipamento, generateUpdateTokenLocker
} from "./configs/functions";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {Helmet} from "react-helmet-async";
import LinkNavegacao from "../../../common/Link";
import PageTitle from "../../../common/PageTitle";
import {FiltersButton} from "../../../common/FiltersButton";
import {EquipmentsTableFilter} from "./components/EquipmentsTableFilter";
import {CustomToolbarExportTable} from "../../../common/CustomToolbarExportTable";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {Pagination} from "../../../common/Pagination";
import {ConfirmGenerateTokenDialog} from "./components/ConfirmGenerateTokenDialog";
import {GenerateTokenDialog} from "./components/GenerateTokenDialog";
import ConfirmDialog from "../../../common/ConfirmDialog";
import {LockerForm} from "./components/LockerForm";
import {LastSyncCell} from "./components/LastSyncCell";
import {EquipmentHistoryDialog} from "./components/EquipmentHistoryDialog";
import {listaProdutos} from "../products/configs/functions";
import {exportConfigToPDF} from "./configs/utils";
import {motoresToReceiptRows, portasToReceiptRows} from "./configs/layoutAdapters";
import {RestockingMachineDialog} from "./components/MachineForm/components/RestockingMachineDialog";
import {RestockingLockerDialog} from "./components/LockerForm/components/RestockingLockerDialog";


const LockerRow = ({
                       data,
                       isSempher,
                       handleOpenEditForm,
                       handleOpenHistory,
                       handleUpdateStatus,
                       handleOpenDeleteDialog,
                       handleOpenConfirmGenerateTokenDialog,
                       handleOpenRestockingDialog,
                       handlePrintConfig
                    }) => {
    const isInactive = !data.status;

    const row = (
        <TableRow>
            <TableCell>{data.nome}</TableCell>
            <TableCell>{data.tagIdentificacao}</TableCell>
            <TableCell>{data.empresa.nome}</TableCell>
            <TableCell>{data.descricao}</TableCell>
            <TableCell>
                <EquipmentConnectionStatus
                    mode={data.mode}
                    status={data.status}
                />
            </TableCell>
            <TableCell>
                <LastSyncCell lastSyncAt={data.lastSyncAt} />
            </TableCell>
            <TableCell>
                <Switch
                    checked={!!data.status}
                    onChange={() => handleUpdateStatus('status', isInactive)}
                    disabled={
                        !isSempher ||
                        !hasPermission(['admin', 'admin_armarios', 'update_armarios'])
                    }
                />
            </TableCell>
            <TableCell>
                <Switch disable checked={!!data.acessoFacial} onChange={() => handleUpdateStatus('acesso_facial', !data.acessoFacial)} disabled={isInactive || !isSempher} />
            </TableCell>
            <TableCell>
                <Switch checked={!!data.acessoRetiradas} onChange={() => handleUpdateStatus('acesso_retiradas', !data.acessoRetiradas)} disabled={isInactive || !isSempher} />
            </TableCell>
            <TableCell align='right' style={{ minWidth: 200 }}>
                <IconButton
                    aria-label="Organizar Equipamento"
                    onClick={() => handleOpenRestockingDialog(data)}
                    disabled={isInactive}
                    sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                >
                    <StockIcon color="primary" />
                </IconButton>

                <IconButton
                    aria-label="Imprimir configurações"
                    onClick={() => handlePrintConfig(data)}
                    disabled={isInactive}
                    sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                >
                    <PictureAsPdf color="primary" />
                </IconButton>

                {isSempher && (
                    <>
                        <IconButton
                            aria-label="Visualizar histórico"
                            onClick={() => handleOpenHistory(data)}
                        >
                            <History color='primary' />
                        </IconButton>

                        <IconButton
                            aria-label="Gerar Token para Armário"
                            onClick={() => handleOpenConfirmGenerateTokenDialog(data)}
                            disabled={isInactive}
                            sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}
                        >
                            <PasswordResetIcon />
                        </IconButton>
                    </>
                )}

                {isSempher ? (
                    <>
                        {hasPermission(["admin", "admin_armarios", "update_amarios"]) && (
                            <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)} disabled={isInactive} sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}>
                                <EditIcon />
                            </IconButton>
                        )}

                        {hasPermission(["admin", "admin_armarios", "delete_armarios"]) && (
                            <IconButton aria-label="Excluir" onClick={() => handleOpenDeleteDialog(data)} disabled={isInactive} sx={{ filter: isInactive ? "grayscale(1) brightness(1.1)" : "" }}>
                                <TrashIcon />
                            </IconButton>
                        )}
                    </>
                ) : (
                    <Button onClick={() => handleOpenEditForm(data)} size='small' color="primary" variant='contained' sx={{minWidth: 30}}>
                        <RemoveRedEye fontSize='small'/>
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );

    if (isInactive) {
        return (
            <Tooltip
                title="Armário inativo — ative-o para liberar as ações"
                placement="top"
                arrow
            >
                {row}
            </Tooltip>
        );
    }

    return row;
}

export function ListOfLockers() {
    const { dadosUsuario } = useAppContext();
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

    const queryClient = useQueryClient();

    const [selectedLocker, setSelectedLocker] = useState(null);

    const [lastUpdated, setLastUpdated] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openGenerateTokenDialog, setOpenGenerateTokenDialog] = useState(false);
    const [openConfirmGenerateTokenDialog, setOpenConfirmGenerateTokenDialog] = useState(false);
    const [openLockerForm, setOpenLockerForm] = useState(false);
    const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
    const [openRestockingDialog, setOpenRestockingDialog] = useState(false);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const columns = useMemo(() => [
        { field: 'nome', headerName: 'Nome' },
        { field: 'tagIdentificaco', headerName: 'Tag Identifcação' },
        { field: 'empresa.fantasyName', headerName: 'Empresa', valueGetter: (params) => params.row.empresa?.nome },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'mode', headerName: 'Conexão', valueGetter: (params) => params.row.mode === 'unknown' ? 'desconhecida' : params.row.mode },
        { field: 'lastSyncAt', headerName: 'Última sincronização', valueGetter: (params) => params.row.lastSyncAt },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status ? 'Ativado' : 'Inativado' },
        { field: 'acessoFacial', headerName: 'Habilitar facial', valueGetter: (params) => params.row.acessoFacial },
        { field: 'acessoRetiradas', headerName: 'Habilitar retirada', valueGetter: (params) => params.row.acessoRetiradas },
    ], []);

    const queryFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        nome: searchParams.get('nome'),
        id_empresas: [searchParams.get('id_empresas')],
        id_ponto_entrega: searchParams.get('id_ponto_entrega'),
        tipo: searchParams.get('tipo'),
        status: searchParams.get('status'),
    };

    const queryKey = ['listaArmarios', queryFilters];
    const { data: armariosData, isLoading, isPending, isSuccess, isRefetching } = useQuery({
        queryKey,
        queryFn: () => carregaLockers(queryFilters),
        refetchInterval: 1000 * 60 * 3, // 3 min
        refetchIntervalInBackground: true,
    });

    const lockers = armariosData?.data?.data ?? [];
    const numTotalItems = armariosData?.data?.numero_total ?? 0;
    const isLoadingList = isLoading || isPending;

    const { data: empresasData } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return listaEmpresas(null, null, null, true);
        },
        staleTime: Infinity,
    });

    const { data: pontosDeEntregaData } = useQuery({
        queryKey: ['listPontosDeEntrega'],
        queryFn: ({ queryKey }) => {
            const [_key, parametro] = queryKey;
            return carregaPontosEntrega({ isFilter: true });
        },
        staleTime: Infinity,
    });

    const handleUpdateStatus = useMutation({
        mutationFn: (data) => atualizaLocker(data.id, data.dataToUpdate),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKey });
            // get from cached data, descobrir forma de encontrar qual item foi atualizado.

            if (typeof variables?.dataToUpdate.status === 'boolean') {
                const status = variables?.dataToUpdate.status ? 'ativado' : 'inativado';
                exibirAlerta('Sucesso', `Armário ${status} com sucesso`, 'success');
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const { mutateAsync: handleGenerateUpdateToken, isPending: isLoadingUpdateToken } = useMutation({
        mutationFn: async (data) => await generateUpdateTokenLocker(data.id),
        onSuccess: (response) => {
            // get token from response
            const token = response?.data?.token;
            if (token) {
                setSelectedLocker((prevState) => ({ ...prevState, token }));
                setOpenGenerateTokenDialog(true);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao gerar token';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const handleEdit = (armario) => {
        setSelectedLocker(armario);
        setOpenLockerForm(true);
    }

    const handleOpenHistory = (locker) => {
        setSelectedLocker(locker);
        setOpenHistoryDialog(true);
    };

    const handleRestocking = (locker) => {
        setSelectedLocker(locker);
        setOpenRestockingDialog(true);
    }

    const handleOpenDeleteDialog = (item) => {
        setSelectedLocker(item);
        setOpenDeleteDialog(true);
    }

    const handleOpenGenerateTokenDialog = async (item) => {
        try {
            exibirDialog('Gerando token. Aguarde...');
            setSelectedLocker(item);
            setOpenConfirmGenerateTokenDialog(false);
            // endpoint generate token
            await handleGenerateUpdateToken({ id: item.id });
        } catch (error) {
            console.error('ListOfArmario ~ error:', error);
        } finally {
            handleCloseDialog();
        }
    };

    const handlePrintConfig = async (locker) => {
        exibirDialog('Gerando PDF das configurações...');

        try {
            const [configRes, produtosRes] = await Promise.all([
                carregaLockers({ id: locker.id }),
                listaProdutos({ id_empresa: locker.empresa?.id, for_locker: 1 }),
            ]);
            const armario = configRes?.data?.data?.[0] ?? null;
            const portas = armario?.portas ?? [];

            const p5 = portas.find(p => (p.numeroLogico ?? p.numero_logico) === 5);
            console.log('PORTA 5 =>', JSON.stringify(p5, null, 2));

            console.log('chaves da porta 5:', Object.keys(p5));
            console.log('configuracoes:', p5.configuracoes);
            console.log('estoque do armario:', armario.estoque);

            const empresaCompleta = empresasData?.data?.data?.find(
                (e) => e.id === locker.empresa?.id
            ) ?? null;

            exportConfigToPDF({
                linhas: portasToReceiptRows(portas),
                produtos: produtosRes?.data?.data ?? [],
                selectedEmpresa: empresaCompleta,
                dadosUsuario,
                titulo: locker.nome,
                unidadeLabel: 'Portas',
                reportName: 'configuracoes_armario',
                reportTitle: 'CONFIGURAÇÕES DO ARMÁRIO',
            });
        } catch (error) {
            exibirAlerta('Erro', 'Não foi possível gerar o PDF das configurações', 'error');
        } finally {
            handleCloseDialog();
        }
    }

    const { mutateAsync: handleDelete, isPending: isLoadingDelete } = useMutation({
        mutationFn: deletaLocker,
        onSuccess: (response, id) => {
            const message = response.data.message ?? 'Armário excluído com sucesso';
            exibirAlerta('Sucesso', message, 'success');

            if (message === 'O armário indicado possui relação com outros itens. Por isso foi inativado') {
                // como o equipamento não é excluído dependendo dos seus relacionamentos,
                // nao é possível saber se excluiu ou se atualizou o status.
                queryClient.invalidateQueries({ queryKey: queryKey });
            } else {
                const cached = queryClient.getQueryData(queryKey);
                const updatedData = {
                    ...cached,
                    data: {
                        ...cached.data,
                        data: cached.data.data.filter((item) => item.id !== id),
                    },
                };
                queryClient.setQueryData(queryKey, updatedData);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setOpenDeleteDialog(false);
            setSelectedLocker(null);
        },
    });

    useEffect(() => {
        checkPermissionsAndRedirect(['admin', 'admin_armarios', 'list_armarios']);

        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));
            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        if (openLockerForm) return;
        setSelectedLocker(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);
    }, [openLockerForm, searchParams]);

    useEffect(() => {
        if (isSuccess && !isRefetching) {
            const date = new Date();
            const formattedDate = date.toLocaleString('pt-BR');
            setLastUpdated(formattedDate);
        }
    }, [isSuccess, isRefetching]);

    return (
        <>
            <Helmet title='Armários' defer={false} />

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="end"
                spacing={3}
            >
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to="/"><Home fontSize="small" /></LinkNavegacao>
                        <LinkNavegacao to="/epis">Gestão de EPI's</LinkNavegacao>
                        <LinkNavegacao to="/epis/equipamentos">Equipamentos</LinkNavegacao>
                        <Typography variant='span'>Armários</Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<LockerIcon fontSize='large' />}
                        title="Armários"
                        description={(dadosUsuario.isSempher ? "Gerencie" : "Visualize") + " os armários das empresas."}
                    />

                    <Grid item xs={12}>
                        <Stack direction='row' spacing={0.5} alignItems='center' p sx={{ pt: 0 }}>
                            <Typography variant='caption' color='textSecondary'>Última atualização: </Typography>
                            {isLoadingList || lastUpdated === null ? (
                                <Skeleton width={110} variant='text' />
                            ) : (
                                <Typography variant='caption' color='textSecondary'>
                                    {lastUpdated}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        {hasPermission(["admin", "admin_armarios", "create_armarios"]) && dadosUsuario.isSempher &&
                            <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={() => setOpenLockerForm(true)}
                                    disabled={!hasPermission(["admin", "admin_armarios", "create_armarios"])}
                                >
                                    Cadastrar Armário
                                </Button>
                            </Typography>
                        }
                    </Stack>
                </Grid>

                <EquipmentsTableFilter
                    isOpenFilters={isOpenFilters}
                    isLoadingList={isLoadingList}
                    empresas={empresasData?.data?.data || []}
                    pontosDeEntrega={pontosDeEntregaData?.data?.data || []}
                />

                <Grid item xs={12}>
                    <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
                        <CustomToolbarExportTable
                            rows={lockers}
                            columns={columns}
                            reportName="relatorio_armarios"
                            reportPdfTitle="RELATÓRIO DE ARMÁRIOS"
                            getDataWithoutPaginate={() => {
                                const { limit, offset, ...data } = queryFilters;
                                return carregaLockers(data);
                            }}
                            getSelectedEmpresa={() => empresasData.data.data.find(empresa => empresa.id === Number(searchParams.get('id_empresas')))}
                        />

                        <Table aria-label="Armários">
                            <TableHead>
                                <TableRow>
                                    {/*<TableCell align="center">Tipo</TableCell>*/}
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Tag Identificação</TableCell>
                                    <TableCell>Empresa</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Conexão</TableCell>
                                    <TableCell>Última Sincronização</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Habilita facial</TableCell>
                                    <TableCell align="center">Habilita retirada</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingList ? (
                                    <SkeletonTableRow
                                        numCells={10}
                                        numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                                        firstCellIsIcon
                                    />
                                ) : lockers.length > 0 ? (
                                    lockers.map((locker) => (
                                        <LockerRow
                                            key={locker.id}
                                            data={locker}
                                            isSempher={dadosUsuario?.isSempher ?? false}
                                            handleOpenEditForm={() => handleEdit(locker)}
                                            handleOpenHistory={() => handleOpenHistory(locker)}
                                            handleUpdateStatus={(field, value) => {
                                                const dataSendUpdate = {
                                                    id: locker.id,
                                                    dataToUpdate: {
                                                        [field]: value
                                                    }
                                                }
                                                handleUpdateStatus.mutateAsync(dataSendUpdate)
                                            }}
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(locker)}
                                            handleOpenConfirmGenerateTokenDialog={() => handleOpenGenerateTokenDialog(locker)}
                                            handleOpenRestockingDialog={() => handleRestocking(locker)}
                                            handlePrintConfig={() => handlePrintConfig(locker)}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={10} />
                                )}
                            </TableBody>
                        </Table>

                        <Pagination
                            page={page}
                            rowsPerPage={rowsPerPage}
                            numTotalItems={numTotalItems}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <LockerForm
                open={openLockerForm}
                setOpen={setOpenLockerForm}
                armario={selectedLocker}
                empresas={empresasData?.data?.data ?? []}
                queryKey={queryKey}
            />

            <ConfirmDialog
                title="Excluir Armário"
                description="Tem certeza que deseja excluir este armário?"
                goAction={() => handleDelete(selectedLocker.id)}
                handleClose={() => {
                    setOpenDeleteDialog(false);
                    setSelectedLocker(null);
                }}
                state={openDeleteDialog}
                isLoading={isLoadingDelete}
            />

            <ConfirmGenerateTokenDialog
                open={openConfirmGenerateTokenDialog}
                selectedEquipment={selectedLocker}
                goAction={() => handleOpenGenerateTokenDialog(selectedLocker)}
                handleClose={() => {
                    setOpenConfirmGenerateTokenDialog(false);
                    setSelectedLocker(null);
                }}
                isLoading={isLoadingUpdateToken}
            />

            <GenerateTokenDialog
                open={openGenerateTokenDialog}
                selectedEquipment={selectedLocker}
                handleClose={() => {
                    setOpenGenerateTokenDialog(false);
                    setSelectedLocker(null);
                }}
            />

            <EquipmentHistoryDialog
                open={openHistoryDialog}
                onClose={() => {
                    setOpenHistoryDialog(false);
                    setSelectedLocker(null);
                }}
                equipamento={selectedLocker}
                tipo="armario"
            />

            <RestockingLockerDialog
                open={openRestockingDialog}
                onClose={() => {
                    setOpenRestockingDialog(false);
                    setSelectedLocker(null);
                }}
                locker={selectedLocker}
            />
        </>
    );
}